// How a user is *presented* anywhere their identity is public-facing
// (comments, likers lists, activity feeds).
//
// SUPER_ADMIN is the one role that never appears as a person: the platform
// owner speaks as the company, so every reader sees the brand logo, "Surya
// Group" and a FOUNDER badge instead of a personal name and photo. Every
// other role (ADMIN, EMPLOYEE, CLIENT) shows their real profile picture,
// full name and role.
//
// This only changes presentation — the underlying user id is untouched, so
// ownership checks (can I delete my own comment?) still work normally.

export const BRAND_NAME = "Surya Group";
export const BRAND_ROLE = "FOUNDER";
export const BRAND_LOGO = "/favicon.jpg";

// `user_role` arrives as a plain string on comment/liker payloads and as a
// relational object ({ role }) on the logged-in user from AuthContext.
export const getRoleName = (user) => {
  if (!user) return null;
  return typeof user.user_role === "string" ? user.user_role : user.user_role?.role || null;
};

const isBrandRole = (role) => role === "SUPER_ADMIN" || role === BRAND_ROLE;

/**
 * @returns {{ name: string, role: string|null, isBrand: boolean, avatarUser: object }|null}
 *   avatarUser — feed straight to <Avatar user={...} />
 */
export const getDisplayIdentity = (user) => {
  if (!user) return null;
  const role = getRoleName(user);

  // The API already resolves a SUPER_ADMIN to the company's name/logo (see
  // shapeUser in knowledge_hub/controller.js) and never leaks the personal
  // one, so prefer what it sends — changing the company logo in the DB then
  // propagates without a deploy — and fall back to the brand constants.
  if (isBrandRole(role)) {
    return {
      name: `${user.first_name || ""} ${user.last_name || ""}`.trim() || BRAND_NAME,
      role: BRAND_ROLE,
      isBrand: true,
      avatarUser: {
        first_name: "Surya",
        last_name: "Group",
        profile_picture_url: user.profile_picture_url || BRAND_LOGO,
      },
    };
  }

  return {
    name: `${user.first_name || ""} ${user.last_name || ""}`.trim() || "Unknown user",
    role,
    isBrand: false,
    avatarUser: user,
  };
};

// FOUNDER is top-of-hierarchy, so it gets the solid gold badge; ADMIN and
// EMPLOYEE read as "the team" via the brand/info tints; CLIENT stays neutral.
export const ROLE_BADGE_STYLES = {
  FOUNDER: "bg-warning text-white",
  ADMIN: "bg-primary_tint text-primary",
  EMPLOYEE: "bg-info_bg text-info",
  CLIENT: "bg-surface_alt text-text_secondary",
};

export const DEFAULT_ROLE_BADGE_STYLE = "bg-surface_alt text-text_secondary";
